import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';

const RegisterContainer = styled.div`
 min-height: 100vh;
 background-color: white;
 font-family: 'Noto Sans Thai', sans-serif !important;
 * {
   font-family: 'Noto Sans Thai', sans-serif !important;
 }
`;

const Header = styled.div`
 background: white;
 padding: 16px 24px;
 display: flex;
 align-items: center;
 gap: 12px;
 box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const Logo = styled.img`
 width: 32px;
 height: 32px;
 object-fit: contain;
`;

const HeaderTitle = styled.h1`
 font-size: 18px;
 font-weight: bold;
 color: #666;
 margin: 0;
 font-family: 'Noto Sans Thai', sans-serif !important;
`;

const StepIndicator = styled.div`
 display: flex;
 align-items: center;
 justify-content: center;
 padding: 24px;
 gap: 16px;
`;

const StepCircle = styled.div`
 width: 40px;
 height: 40px;
 border-radius: 50%;
 display: flex;
 align-items: center;
 justify-content: center;
 font-size: 18px;
 font-weight: 600;
 font-family: 'Noto Sans Thai', sans-serif !important;
 
 ${props => props.active ? `
   background-color: #00BBD3;
   color: white;
 ` : `
   background-color: #e1e5e9;
   color: #999;
 `}
`;

const StepLine = styled.div`
  width: 60px;
  height: 4px;
  background-color: ${props => props.active ? '#00BBD3' : '#e1e5e9'};
  border-radius: 2px;
  transition: background-color 0.3s ease;
`;
const Content = styled.div`
 padding: 24px;
 max-width: 400px;
 margin: 0 auto;
`;

const SectionTitle = styled.h2`
 font-size: 20px;
 font-weight: 600;
 color: #00BBD3;
 margin-bottom: 24px;
 font-family: 'Noto Sans Thai', sans-serif !important;
`;

const Section = styled.div`
 margin-bottom: 40px;
`;

const RadioGroup = styled.div`
 display: flex;
 align-items: center;
 gap: 12px;
 cursor: pointer;
 padding: 12px 16px;
 border-radius: 12px;
 transition: background-color 0.2s ease;
 min-height: 50px;
 margin-bottom: 16px;
 
 &:hover {
   background-color: #f0f9ff;
 }
`;

const RadioInput = styled.input.attrs({ type: 'radio' })`
 width: 24px;
 height: 24px;
 border: 3px solid #00BBD3;
 border-radius: 50%;
 background-color: white;
 cursor: pointer;
 appearance: none;
 position: relative;
 transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
 transform: scale(1);
 flex-shrink: 0;
 
 &:hover {
   transform: scale(1.05);
   border-color: #009bb3;
 }
 
 &:checked {
   background-color: #00BBD3;
   transform: scale(1.1);
 }
 
 &:checked::after {
   content: '';
   position: absolute;
   top: 50%;
   left: 50%;
   transform: translate(-50%, -50%);
   width: 10px;
   height: 10px;
   background-color: white;
   border-radius: 50%;
   animation: checkPulse 0.3s ease;
 }
 
 &:active {
   transform: scale(0.95);
 }
`;

const RadioLabel = styled.label`
 font-size: 18px;
 color: #666666;
 cursor: pointer;
 font-weight: bold;
 font-family: 'Noto Sans Thai', sans-serif !important;
 line-height: 1.4;
 flex: 1;
`;

const SubSection = styled.div`
 margin-left: 40px;
 margin-top: 16px;
 max-height: ${props => props.show ? '400px' : '0'};
 overflow: hidden;
 opacity: ${props => props.show ? '1' : '0'};
 transform: translateY(${props => props.show ? '0' : '-10px'});
 transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
`;

const SubText = styled.p`
 font-size: 16px;
 color: #666666;
 margin-bottom: 16px;
 font-weight: bold;
 font-family: 'Noto Sans Thai', sans-serif !important;
`;

const InputRow = styled.div`
 display: flex;
 align-items: center;
 gap: 12px;
 margin-bottom: 16px;
`;

const SmallInput = styled.input`
 width: 80px;
 padding: 12px 16px;
 border: 2px solid #e1e5e9;
 border-radius: 12px;
 font-size: 16px;
 color: #666666;
 background-color: white;
 transition: border-color 0.3s ease;
 font-family: 'Noto Sans Thai', sans-serif !important;
 font-weight: bold;
 text-align: center;

 &:focus {
   outline: none;
   border-color: #00BBD3;
 }
`;

const UnitLabel = styled.span`
 font-size: 16px;
 color: #666;
 font-weight: bold;
 font-family: 'Noto Sans Thai', sans-serif !important;
`;

const ButtonRow = styled.div`
 display: flex;
 gap: 16px;
 margin-top: 40px;
`;

const BackButton = styled.button`
 background-color: white;
 color: #00BBD3;
 border: 2px solid #00BBD3;
 border-radius: 30px;
 padding: 18px 32px;
 font-size: 18px;
 font-weight: bold;
 cursor: pointer;
 transition: all 0.3s ease;
 flex: 1;
 font-family: 'Noto Sans Thai', sans-serif !important;
 box-shadow: 0 9px 14px 0 rgba(0, 187, 211, 0.08);

 &:hover {
   background-color: #00BBD3;
   color: white;
 }

 &:active {
   background-color: #009bb3;
 }
`;

const NextButton = styled.button`
 background-color: #00BBD3;
 color: white;
 border: none;
 border-radius: 30px;
 padding: 18px 32px;
 font-size: 18px;
 font-weight: bold;
 cursor: pointer;
 transition: all 0.3s ease;
 flex: 1;
 font-family: 'Noto Sans Thai', sans-serif !important;
 box-shadow: 0 9px 14px 0 rgba(0, 187, 211, 0.08);

 &:hover {
   background-color: #009bb3;
 }

 &:active {
   background-color: #008aa0;
 }
`;

const CheckPulse = `
 @keyframes checkPulse {
   0% {
     transform: translate(-50%, -50%) scale(0);
     opacity: 0;
   }
   50% {
     transform: translate(-50%, -50%) scale(1.2);
     opacity: 0.8;
   }
   100% {
     transform: translate(-50%, -50%) scale(1);
     opacity: 1;
   }
 }
`;

const GlobalStyles = `
 ${CheckPulse}
`;

const RegisterStep2 = () => {
 const navigate = useNavigate();
 const location = useLocation();
 const previousData = location.state || {};

 const [alcoholData, setAlcoholData] = useState({
   status: '',
   amount: '',
   frequency: ''
 });

 const [smokingData, setSmokingData] = useState({
   status: '',
   amount: '',
   duration: '',
   quitAmount: ''
 });

 const handleAlcoholChange = (field, value) => {
   setAlcoholData(prev => ({
     ...prev,
     [field]: value
   }));
 };

 const handleSmokingChange = (field, value) => {
   setSmokingData(prev => ({
     ...prev,
     [field]: value
   }));
 };

 const handleAlcoholRadioClick = (value) => {
   handleAlcoholChange('status', value);
 };

 const handleSmokingRadioClick = (value) => {
   handleSmokingChange('status', value);
 };

 const handleBack = () => {
   navigate('/register/step1');
 };

 const handleNext = () => {
   if (!alcoholData.status || !smokingData.status) {
     alert('กรุณาเลือกข้อมูลการดื่มแอลกอฮอล์และการสูบบุหรี่');
     return;
   }

   if (alcoholData.status === 'ดื่มเป็นประจำ' && (!alcoholData.amount || !alcoholData.frequency)) {
     alert('กรุณากรอกข้อมูลการดื่มแอลกอฮอล์ให้ครบถ้วน');
     return;
   }

   if (smokingData.status === 'สูบบุหรี่' && (!smokingData.amount || !smokingData.duration)) {
     alert('กรุณากรอกข้อมูลการสูบบุหรี่ให้ครบถ้วน');
     return;
   }

   if (smokingData.status === 'หยุดสูบบุหรี่' && (!smokingData.amount || !smokingData.duration || !smokingData.quitAmount)) {
     alert('กรุณากรอกข้อมูลการหยุดสูบบุหรี่ให้ครบถ้วน');
     return;
   }

   navigate('/register/step3', { 
     state: { 
       ...previousData, 
       step2Data: { alcoholData, smokingData } 
     } 
   });
 };

 return (
   <>
     <style>{GlobalStyles}</style>
     <RegisterContainer>
       <Header>
         <Logo src="./logo.png" alt="MFU Logo" />
         <HeaderTitle>MFU Wellness Center</HeaderTitle>
       </Header>

       <StepIndicator>
        <StepCircle active>1</StepCircle>
        <StepLine active />
        <StepCircle active>2</StepCircle>
        <StepLine />
        <StepCircle>3</StepCircle>
        </StepIndicator>

       <Content>
         <Section>
           <SectionTitle>การดื่มแอลกอฮอล์</SectionTitle>
           
           <RadioGroup onClick={() => handleAlcoholRadioClick('ไม่เคยดื่ม')}>
             <RadioInput
               name="alcohol"
               value="ไม่เคยดื่ม"
               checked={alcoholData.status === 'ไม่เคยดื่ม'}
               onChange={(e) => handleAlcoholChange('status', e.target.value)}
             />
             <RadioLabel>ไม่เคยดื่ม</RadioLabel>
           </RadioGroup>

           <RadioGroup onClick={() => handleAlcoholRadioClick('ดื่มเป็นบางโอกาส')}>
             <RadioInput
               name="alcohol"
               value="ดื่มเป็นบางโอกาส"
               checked={alcoholData.status === 'ดื่มเป็นบางโอกาส'}
               onChange={(e) => handleAlcoholChange('status', e.target.value)}
             />
             <RadioLabel>ดื่มเป็นบางโอกาส</RadioLabel>
           </RadioGroup>

           <RadioGroup onClick={() => handleAlcoholRadioClick('ดื่มเป็นประจำ')}>
             <RadioInput
               name="alcohol"
               value="ดื่มเป็นประจำ"
               checked={alcoholData.status === 'ดื่มเป็นประจำ'}
               onChange={(e) => handleAlcoholChange('status', e.target.value)}
             />
             <RadioLabel>ดื่มเป็นประจำ</RadioLabel>
           </RadioGroup>

           <SubSection show={alcoholData.status === 'ดื่มเป็นประจำ'}>
             <SubText>คุณเริ่มดื่มแอลกอฮอล์เมื่ออายุ</SubText>
             <InputRow>
               <SmallInput
                 type="number"
                 value={alcoholData.amount}
                 onChange={(e) => handleAlcoholChange('amount', e.target.value)}
               />
               <UnitLabel>ปี</UnitLabel>
             </InputRow>

             <SubText>โดยเฉลี่ยปัจจุบันดื่มแอลกอฮอล์</SubText>
             <InputRow>
               <SmallInput
                 type="number"
                 value={alcoholData.frequency}
                 onChange={(e) => handleAlcoholChange('frequency', e.target.value)}
               />
               <UnitLabel>ขวด/วัน</UnitLabel>
             </InputRow>
           </SubSection>

           <RadioGroup onClick={() => handleAlcoholRadioClick('หยุดดื่ม')}>
             <RadioInput
               name="alcohol"
               value="หยุดดื่ม"
               checked={alcoholData.status === 'หยุดดื่ม'}
               onChange={(e) => handleAlcoholChange('status', e.target.value)}
             />
             <RadioLabel>หยุดดื่ม</RadioLabel>
           </RadioGroup>

           <SubSection show={alcoholData.status === 'หยุดดื่ม'}>
             <SubText>คุณเริ่มดื่มแอลกอฮอล์เมื่ออายุ</SubText>
             <InputRow>
               <SmallInput
                 type="number"
                 value={alcoholData.amount}
                 onChange={(e) => handleAlcoholChange('amount', e.target.value)}
               />
               <UnitLabel>ปี</UnitLabel>
             </InputRow>

             <SubText>โดยเฉลี่ยปัจจุบันดื่มแอลกอฮอล์</SubText>
             <InputRow>
               <SmallInput
                 type="number"
                 value={alcoholData.frequency}
                 onChange={(e) => handleAlcoholChange('frequency', e.target.value)}
               />
               <UnitLabel>ขวด/วัน</UnitLabel>
             </InputRow>

             <SubText>คุณหยุดดื่มแอลกอฮอล์เมื่ออายุ</SubText>
             <InputRow>
               <SmallInput
                 type="number"
                 value={alcoholData.quitAge}
                 onChange={(e) => handleAlcoholChange('quitAge', e.target.value)}
               />
               <UnitLabel>ปี</UnitLabel>
             </InputRow>
           </SubSection>
         </Section>

         <Section>
           <SectionTitle>การสูบบุหรี่</SectionTitle>
           
           <RadioGroup onClick={() => handleSmokingRadioClick('ไม่เคยสูบ')}>
             <RadioInput
               name="smoking"
               value="ไม่เคยสูบ"
               checked={smokingData.status === 'ไม่เคยสูบ'}
               onChange={(e) => handleSmokingChange('status', e.target.value)}
             />
             <RadioLabel>ไม่เคยสูบ</RadioLabel>
           </RadioGroup>

           <RadioGroup onClick={() => handleSmokingRadioClick('สูบบุหรี่')}>
             <RadioInput
               name="smoking"
               value="สูบบุหรี่"
               checked={smokingData.status === 'สูบบุหรี่'}
               onChange={(e) => handleSmokingChange('status', e.target.value)}
             />
             <RadioLabel>สูบบุหรี่</RadioLabel>
           </RadioGroup>

           <SubSection show={smokingData.status === 'สูบบุหรี่'}>
             <SubText>คุณเริ่มสูบบุหรี่เมื่ออายุ</SubText>
             <InputRow>
               <SmallInput
                 type="number"
                 value={smokingData.amount}
                 onChange={(e) => handleSmokingChange('amount', e.target.value)}
               />
               <UnitLabel>ปี</UnitLabel>
             </InputRow>

             <SubText>โดยเฉลี่ยปัจจุบันคุณสูบบุหรี่</SubText>
             <InputRow>
               <SmallInput
                 type="number"
                 value={smokingData.duration}
                 onChange={(e) => handleSmokingChange('duration', e.target.value)}
               />
               <UnitLabel>ขวด/วัน</UnitLabel>
             </InputRow>
           </SubSection>

           <RadioGroup onClick={() => handleSmokingRadioClick('หยุดสูบบุหรี่')}>
             <RadioInput
               name="smoking"
               value="หยุดสูบบุหรี่"
               checked={smokingData.status === 'หยุดสูบบุหรี่'}
               onChange={(e) => handleSmokingChange('status', e.target.value)}
             />
             <RadioLabel>หยุดสูบบุหรี่</RadioLabel>
           </RadioGroup>

           <SubSection show={smokingData.status === 'หยุดสูบบุหรี่'}>
             <SubText>คุณเริ่มสูบบุหรี่เมื่ออายุ</SubText>
             <InputRow>
               <SmallInput
                 type="number"
                 value={smokingData.amount}
                 onChange={(e) => handleSmokingChange('amount', e.target.value)}
               />
               <UnitLabel>ปี</UnitLabel>
             </InputRow>

             <SubText>คุณหยุดสูบบุหรี่เมื่ออายุ</SubText>
             <InputRow>
               <SmallInput
                 type="number"
                 value={smokingData.duration}
                 onChange={(e) => handleSmokingChange('duration', e.target.value)}
               />
               <UnitLabel>ปี</UnitLabel>
             </InputRow>

             <SubText>โดยเฉลี่ยปัจจุบันคุณสูบบุหรี่</SubText>
             <InputRow>
               <SmallInput
                 type="number"
                 value={smokingData.quitAmount}
                 onChange={(e) => handleSmokingChange('quitAmount', e.target.value)}
               />
               <UnitLabel>ขวด/วัน</UnitLabel>
             </InputRow>
           </SubSection>
         </Section>

         <ButtonRow>
           <BackButton onClick={handleBack}>
             ก่อนหน้า
           </BackButton>
           <NextButton onClick={handleNext}>
             ถัดไป
           </NextButton>
         </ButtonRow>
       </Content>
     </RegisterContainer>
   </>
 );
};

export default RegisterStep2;